import { useEffect, useRef, useState } from "react";
import EditionBadge from "./EditionBadge";
import { apiFetchJson } from "../apiClient";
import "./DocumentEdition.css";

/** Edition controls for one document: confirm an auto-detected edition, edit
 * it, or clear it. Controls are disabled while a request is in flight and only
 * shown when the backend says the user may edit (doc.can_edit); otherwise the
 * read-only badge is shown. */
export default function DocumentEdition({ doc, database, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const save = async (nextEdition) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await apiFetchJson(
        `/api/v1/documents/${encodeURIComponent(doc.document_id)}/edition?database=${encodeURIComponent(database)}`,
        { method: "PATCH", body: JSON.stringify({ edition: nextEdition }) },
      );
      if (!mounted.current) return;
      setEditing(false);
      onChanged?.();
    } catch (err) {
      if (mounted.current) setError(err.message || "Failed to update edition");
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const startEdit = () => {
    setValue(doc.edition || "");
    setError(null);
    setEditing(true);
  };

  const hasEdition = Boolean(doc.edition && doc.edition.trim());

  return (
    <span className="doc-edition">
      {editing ? (
        <>
          <input
            type="text"
            className="ds-input doc-edition-input"
            aria-label="Edition"
            // Mirrors EDITION_MAX_LENGTH on the backend (frontend has no settings access).
            maxLength={200}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            dir="auto"
          />
          <button
            type="button"
            className="ds-btn ds-btn--primary ds-btn--sm"
            disabled={busy}
            onClick={() => save(value.trim() || null)}
          >
            Save
          </button>
          <button
            type="button"
            className="ds-btn ds-btn--ghost ds-btn--sm"
            disabled={busy}
            onClick={() => {
              setEditing(false);
              setError(null);
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          {hasEdition ? (
            <EditionBadge edition={doc.edition} source={doc.edition_source} />
          ) : (
            <span className="doc-edition-empty">No edition</span>
          )}
          {doc.can_edit && hasEdition && doc.edition_source === "auto" && (
            <button
              type="button"
              className="ds-btn ds-btn--secondary ds-btn--sm"
              disabled={busy}
              onClick={() => save(doc.edition)}
            >
              Confirm
            </button>
          )}
          {doc.can_edit && (
            <button
              type="button"
              className="ds-btn ds-btn--secondary ds-btn--sm"
              disabled={busy}
              onClick={startEdit}
            >
              Edit
            </button>
          )}
          {doc.can_edit && hasEdition && (
            <button
              type="button"
              className="ds-btn ds-btn--ghost ds-btn--sm"
              disabled={busy}
              onClick={() => save(null)}
            >
              Clear
            </button>
          )}
        </>
      )}
      {error && (
        <span role="alert" className="doc-edition-error">
          {error}
        </span>
      )}
    </span>
  );
}
